<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed z-[9999] w-64 rounded-lg border border-gray-200 bg-white p-4 shadow-xl dark:border-gray-700 dark:bg-slate-800"
      :style="position"
    >
    <div class="mb-3">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-white">
        {{ changes.length }} Unpublished {{ changes.length === 1 ? 'Change' : 'Changes' }}
      </h3>
      <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Click to prepare publishing your changes to your custom snippet to appear on your website.
      </p>
    </div>

    <div class="max-h-64 space-y-2 overflow-y-auto">
      <div
        v-for="(change, index) in changes"
        :key="index"
        class="flex items-start space-x-2 text-xs"
      >
        <span
          :class="[
            'mt-0.5 flex-shrink-0 rounded px-1.5 py-0.5 font-medium',
            change.type === 'added'
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : change.type === 'modified'
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
          ]"
        >
          {{ change.type === 'added' ? '+' : change.type === 'modified' ? '~' : '-' }}
        </span>
        <div class="flex-1 min-w-0">
          <div class="font-medium text-gray-900 dark:text-white truncate">
            {{ getCleanName(change) }}
          </div>
          <div class="text-gray-500 dark:text-gray-400">
            {{ change.category }}
          </div>
          <div v-if="change.details && change.details.length > 0" class="mt-0.5 text-gray-400 dark:text-gray-500">
            {{ getCleanDetails(change) }}
          </div>
        </div>
      </div>
    </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import type { ConfigChange } from '@/composables/useConfigChanges'
import { stripHtml } from '@/services/string'

interface Props {
  show: boolean
  changes: ConfigChange[]
  position?: 'left' | 'right'
  buttonRef?: HTMLElement | null
}

const props = withDefaults(defineProps<Props>(), {
  position: 'left',
  buttonRef: null
})

// Helper to get clean display name without HTML tags
const getCleanName = (change: ConfigChange): string => {
  return stripHtml(change.name)
}

// Helper to get clean details without HTML tags
const getCleanDetails = (change: ConfigChange): string => {
  if (!change.details || change.details.length === 0) return ''
  return change.details.map(detail => stripHtml(detail)).join(', ')
}

const position = computed(() => {
  if (!props.buttonRef || !props.show) {
    return { display: 'none' }
  }

  const rect = props.buttonRef.getBoundingClientRect()
  const previewWidth = 256 // w-64 = 16rem = 256px
  const gap = 8

  if (props.position === 'left') {
    // PublishIndicator (left sidebar) - show to the RIGHT of the button
    return {
      left: `${rect.right + gap}px`,
      top: `${rect.top}px`
    }
  } else {
    // PublishButton (top nav) - show to the RIGHT of the button
    return {
      left: `${rect.right + gap}px`,
      top: `${rect.top}px`
    }
  }
})
</script>
